import React from 'react';

import * as ArchiveActions from 'archive/ArchiveActions';
import {ArchiveRecord} from 'archive/ArchiveConstants';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {
  InventoryFeature,
  RegistryFeature,
  StandardFeatureDefinitionWithItem,
} from 'auth/capabilities/CapabilityFeatureConstants';
import {areAnyFeaturesEnabled, isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import PermissionConstants, {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {NumberIDFolder, StringIDFolder} from 'constants/FolderConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderSelectorModal from 'file-browser/FolderSelectorModal';
import {getExportURL} from 'folder-items/FileUtil';
import * as BioentityActions from 'inventory/BioentityActions';
import PrintBarcodeModal from 'label-printing/PrintBarcodeModal';
import ExportMoleculeModal from 'metadata/ExportMoleculeModal';
import ExportSequenceModal from 'metadata/ExportSequenceModal';
import useIsComplexPolymerQuery from 'modbio/complex-polymers/useIsComplexPolymerQuery';
import NavStore from 'nav/NavStore';
import RegistryActions from 'registry/RegistryActions';
// @ts-expect-error
import {openSetCollectionModal} from 'registry/SetCollectionModal';
import EntityStore from 'stores/EntityStore';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import LegacyDropdownMenu from 'ui-library/dropdowns/LegacyDropdownMenu';
import LegacyDropdownMenuItem from 'ui-library/dropdowns/LegacyDropdownMenuConstants';
import runModal from 'ui-library/runModal';
import licenses from 'user/Licenses';
import APIUtil from 'util/APIUtil';
import {submitCSRFForm} from 'util/Net';
import AddToWorklistButton from 'worklists/AddToWorklistButton';
import {allIdsSupported} from 'worklists/WorklistUtil';

export interface MetadataEditorToolbarProps {
  archiveRecord?: ArchiveRecord | null;
  fileId: string;
  fileName: string;
  folder?: NumberIDFolder | StringIDFolder;
  hasRegistrySupport: boolean;
  isBioentityFailed: boolean;
  onRegister: () => void;
  permissions: Permissions;
  projectFolder?: NumberIDFolder | StringIDFolder | null;
  registryId?: string | null;
  schemaId?: string | null;
  schemaRegistryId?: string | null;
  schemaPermissions?: {objectsRegisterable: boolean} | null;
  additionalMenuItems?: Array<LegacyDropdownMenuItem>;
  editURL?: string;
  shouldAllowChangeCollection: boolean;
  disableRegistering?: boolean;

  // Whether or not the object is a non-interactive Process (aka TaskGroup)
  // Non-interactive processes cannot be edited or archived. The only edit action available is to move the process
  // to a different folder as an escape hatch to handle permissions issues.
  isNonInteractiveProcess?: boolean;
}

export class MetadataEditorToolbar extends React.Component<MetadataEditorToolbarProps> {
  private handleChangeCollection = (): void => {
    openSetCollectionModal({files: [{id: this.props.fileId, name: this.props.fileName}]});
  };

  private isRegistered = (): boolean => this.props.registryId != null;

  private getPartialFeatureDefinition = (
    permissions?: Permissions
  ): Omit<StandardFeatureDefinitionWithItem, 'feature'> => ({
    item: {
      id: this.props.fileId,
      permissions,
    },

    itemState: {isRegistered: this.isRegistered()},
  });

  private handleOpenForMove = (isUnregister: boolean): void => {
    const originalFolder = this.isRegistered() ? this.props.projectFolder : this.props.folder;
    const files = [{id: this.props.fileId}];
    FolderSelectorModal.openForMove(
      {
        allowNone:
          this.isRegistered() &&
          !BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_REGISTRY_FILES &&
          !isUnregister,
        initialSelectedFolderId: originalFolder ? originalFolder.api_identifier : undefined,
        itemsToMove: files,
        showInventory: true,
        showNotebook: true,
        showRegistries: false,
      },
      (folderId) => {
        const parentFolderIds = [this.props.folder!.api_identifier];
        if (isUnregister) {
          if (this.props.projectFolder != null) {
            parentFolderIds.push(this.props.projectFolder.api_identifier);
          }
          RegistryActions.unregisterWithDataMods([this.props.fileId], folderId, parentFolderIds);
        } else {
          FileBrowserActions.moveItemsWithDataMods(
            [this.props.fileId],
            folderId,
            {parentFolderIds, hasRegisteredItems: this.isRegistered()},
            // `shortcutPredicate` - if the file is registered, tell `moveItems` to change project shortcut
            () => this.isRegistered()
          );
        }
      }
    );
  };

  private makeExempt = (): void => {
    BioentityActions.overrideValidationStatus([this.props.fileId]);
  };

  private renderMoreFileOptions = (): JSX.Element | null => {
    const menuItems = [];
    if (APIUtil.isNucleotideSequence(this.props.fileId)) {
      menuItems.push({
        name: 'Export sequence',
        handleOnClick: () => runModal(ExportSequenceModal, {editURL: this.props.editURL!}),
        dataTestAttrs: {component: 'OverflowDropdownButton', element: 'exportSequence'},
      });
    }
    if (APIUtil.isMolecule(this.props.fileId)) {
      menuItems.push({
        name: 'Export molecule',
        handleOnClick: () => runModal(ExportMoleculeModal, {editURL: this.props.editURL!}),
        dataTestAttrs: {component: 'OverflowDropdownButton', element: 'exportMolecule'},
      });
    }
    if (APIUtil.isBulkAssembly(this.props.fileId)) {
      // TODO(BNCH-59006): Actually do export
      menuItems.push({
        name: 'Export assembly',
        disabled: true,
        tooltipText: 'Export is not yet available for Assemblies.',
      });
    }
    if (licenses.hasEnterprisePlan()) {
      menuItems.push({
        name: EXPORT_AUDIT_LOG_DISPLAY_STRING,
        handleOnClick: () => {
          AuditActions.exportAuditLogs([this.props.fileId]);
        },
        dataTestAttrs: {component: 'OverflowDropdownButton', element: 'exportAuditLog'},
      });
    }
    if (this.props.permissions.admin && this.props.isBioentityFailed) {
      menuItems.push({name: 'Override validation checks', handleOnClick: this.makeExempt});
    }
    if (isFeatureEnabled({feature: InventoryFeature.INVENTORY_EXPORT}) && this.props.registryId) {
      menuItems.push({
        name: 'Print registry labels',
        handleOnClick: () => runModal(PrintBarcodeModal, {itemIds: [this.props.fileId]}),
      });
    }
    if (this.props.additionalMenuItems != null) {
      menuItems.push(...this.props.additionalMenuItems);
    }

    if (menuItems.length) {
      return (
        <Tooltip container="body" placement={TooltipPlacement.TOP} title="More">
          <span className="dropdown">
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              hideDropdownCaret={true}
              className="metadata-header-button-spacing-left"
              icon="overflow"
              isDropdown={true}
              size={ButtonSize.SMALL}
            />
            <LegacyDropdownMenu className="dropdown-menu--dropLeft" items={[menuItems]} />
          </span>
        </Tooltip>
      );
    }
    return null;
  };

  render(): JSX.Element {
    const schemaRegistryId = this.props.schemaRegistryId;
    // Show register button only if:
    // (1) unregistered
    // (2) can write to file
    // (3) the file's schema permits registration and the schema's registry permits append (If file's schema
    //     is null, then show button anyway - disabled with an error banner.)
    const shouldShowRegister =
      this.props.registryId == null &&
      this.props.hasRegistrySupport &&
      (this.props.schemaPermissions == null || this.props.schemaPermissions.objectsRegisterable) &&
      (schemaRegistryId == null || NavStore.hasSourceAuth(schemaRegistryId, PermissionConstants.APPEND)) &&
      isFeatureEnabled({
        feature: RegistryFeature.REGISTRY_REGISTER,
        item: {
          id: this.props.fileId,
          permissions: this.props.permissions,
        },
      });
    const shouldShowUnregister = isFeatureEnabled({
      feature: RegistryFeature.REGISTRY_UNREGISTER,
      item: {
        id: this.props.fileId,
        permissions: this.props.permissions,
      },
    });

    const shouldShowArchiveOrUnarchive =
      BenchlingConfig.ENABLE_ARCHIVING_V2 &&
      areAnyFeaturesEnabled([
        {
          feature: StandardFeature.ARCHIVE,
          ...this.getPartialFeatureDefinition(this.props.permissions),
        },
        {
          feature: StandardFeature.UNARCHIVE,
          ...this.getPartialFeatureDefinition(this.props.permissions),
        },
      ]) &&
      !this.props.isNonInteractiveProcess &&
      // Don't show archive button on metadata panel if flag is not enabled
      !(APIUtil.isTemplateCollectible(this.props.fileId) && !BenchlingConfig.ELN_ENABLE_TEMPLATE_ARCHIVING) &&
      // Write actions on existing molecules should be disabled when chemistry is disabled
      (!APIUtil.isMolecule(this.props.fileId) || BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE);

    return (
      <div className="metadata-header-section u-justify-content-end">
        {this.props.registryId &&
          this.props.shouldAllowChangeCollection &&
          isFeatureEnabled({...this.getPartialFeatureDefinition(), feature: StandardFeature.EDIT}) && (
            <Tooltip container="body" placement={TooltipPlacement.TOP} title="Change collection...">
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                className="metadata-header-button-spacing-left"
                icon="add-collection"
                onClick={this.handleChangeCollection}
                size={ButtonSize.SMALL}
              />
            </Tooltip>
          )}
        {APIUtil.isFolderContainable(this.props.fileId) &&
          isFeatureEnabled({
            feature: StandardFeature.MOVE,
            ...this.getPartialFeatureDefinition(),
          }) && (
            <Tooltip container="body" placement={TooltipPlacement.TOP} title="Move to folder">
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                className="metadata-header-button-spacing-left"
                disabled={!this.props.permissions.movable}
                icon="move"
                onClick={() => {
                  this.handleOpenForMove(false);
                }}
                size={ButtonSize.SMALL}
              />
            </Tooltip>
          )}
        {BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS && allIdsSupported([this.props.fileId]) && (
          <AddToWorklistButton
            className="u-mini-spacing-left"
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            disabled={!EntityStore.currentUser()}
            ids={[this.props.fileId]}
            refreshOptions={{refreshRecentWorklists: true}}
          />
        )}
        {(() => {
          if (shouldShowRegister) {
            return (
              <Button
                buttonStyle={ButtonStyle.SECONDARY}
                className="metadata-header-button-spacing-left"
                key="register"
                onClick={this.props.onRegister}
                size={ButtonSize.SMALL}
                text="Register"
                disabled={this.props.disableRegistering}
                dataTestAttrs={{component: 'MetadataEditorToolbar', element: 'registerButton'}}
              />
            );
          } else if (shouldShowUnregister) {
            return (
              <Tooltip container="body" placement={TooltipPlacement.TOP} title="Unregister">
                <Button
                  buttonStyle={ButtonStyle.NEGATIVE}
                  className="metadata-header-button-spacing-left"
                  icon="unregister"
                  key="unregister"
                  onClick={() => {
                    this.handleOpenForMove(true);
                  }}
                  size={ButtonSize.SMALL}
                  dataTestAttrs={{component: 'MetadataEditorToolbar', element: 'unregisterButton'}}
                />
              </Tooltip>
            );
          }
          return null;
        })()}
        {shouldShowArchiveOrUnarchive && (
          <ArchiveOrUnarchiveButton
            className="metadata-header-button-spacing-left"
            isArchivable={Boolean(this.props.permissions.archivable)}
            isUnarchivable={Boolean(this.props.permissions.unarchivable)}
            item={{
              id: this.props.fileId,
              name: this.props.fileName,
              archiveRecord: this.props.archiveRecord,
              projectFolder: this.props.projectFolder,
            }}
            onArchive={ArchiveActions.bulkArchiveFilesWithDataMods}
            onUnarchive={ArchiveActions.bulkUnarchiveFilesWithDataMods}
          />
        )}
        {this.renderMoreFileOptions()}
      </div>
    );
  }
}

export default function QueryingMetadataEditorToolbar(props: MetadataEditorToolbarProps): JSX.Element {
  const additionalMenuItems: Array<LegacyDropdownMenuItem> = [];
  const isComplexPolymer = useIsComplexPolymerQuery(props.fileId, props.schemaId ?? null);
  if (isComplexPolymer) {
    additionalMenuItems.push({
      name: 'Export as HELM',
      handleOnClick: () => {
        const exportURL = getExportURL(props.editURL!, 'helm', {dl: true});
        submitCSRFForm('POST', exportURL, {target: '_blank'});
      },
    });
  }
  return (
    <MetadataEditorToolbar
      {...props}
      additionalMenuItems={[...(props.additionalMenuItems ?? []), ...additionalMenuItems]}
    />
  );
}
